import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Building2,
  CheckCircle2,
  CirclePlus,
  Edit3,
  Hospital,
  MapPin,
  Power,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";

import useFacilities from "../../hooks/useFacilities";

import Button from "../../components/common/Button";
import Modal from "../../components/common/Modal";
import SearchBox from "../../components/common/SearchBox";
import StatusBadge from "../../components/common/StatusBadge";
import ConfirmDialog from "../../components/common/ConfirmDialog";

import DataTable from "../../components/tables/DataTable";
import TableToolbar from "../../components/tables/TableToolbar";

import TextInput from "../../components/forms/TextInput";
import SelectInput from "../../components/forms/SelectInput";
import ToggleSwitch from "../../components/forms/ToggleSwitch";

import "./FacilityManagement.css";



const EMPTY_FORM = {
  facilityName: "",
  legalName: "",
  facilityType: "",
  contactPerson: "",
  email: "",
  phone: "",
  fax: "",
  website: "",
  address: "",
  city: "",
  province: "",
  postalCode: "",
  country: "Canada",
  description: "",
  notes: "",
  status: "active",
  approvalStatus: "pending",
  isActive: true,
};

const FACILITY_TYPES = [
  {
    value: "Hospital",
    label: "Hospital",
  },
  {
    value: "Long-Term Care",
    label: "Long-Term Care",
  },
  {
    value: "Retirement Home",
    label: "Retirement Home",
  },
  {
    value: "Clinic",
    label: "Clinic",
  },
  {
    value: "Home Care",
    label: "Home Care",
  },
  {
    value: "Rehabilitation Centre",
    label: "Rehabilitation Centre",
  },
  {
    value: "Mental Health Centre",
    label: "Mental Health Centre",
  },
  {
    value: "Community Health Centre",
    label: "Community Health Centre",
  },
];

const PROVINCES = [
  { value: "Alberta", label: "Alberta" },
  {
    value: "British Columbia",
    label: "British Columbia",
  },
  { value: "Manitoba", label: "Manitoba" },
  {
    value: "New Brunswick",
    label: "New Brunswick",
  },
  {
    value: "Newfoundland and Labrador",
    label: "Newfoundland and Labrador",
  },
  {
    value: "Nova Scotia",
    label: "Nova Scotia",
  },
  { value: "Ontario", label: "Ontario" },
  {
    value: "Prince Edward Island",
    label: "Prince Edward Island",
  },
  { value: "Quebec", label: "Quebec" },
  {
    value: "Saskatchewan",
    label: "Saskatchewan",
  },
  {
    value: "Northwest Territories",
    label: "Northwest Territories",
  },
  { value: "Nunavut", label: "Nunavut" },
  { value: "Yukon", label: "Yukon" },
];

const APPROVAL_OPTIONS = [
  {
    value: "pending",
    label: "Pending",
  },
  {
    value: "approved",
    label: "Approved",
  },
  {
    value: "rejected",
    label: "Rejected",
  },
];

const FacilityManagement = () => {
  const {
    facilities,
    statistics,
    loading,
    error,

    clearError,

    loadFacilities,
    createFacility,
    updateFacility,
    setFacilityStatus,
  } = useFacilities();

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    facilityTypeFilter,
    setFacilityTypeFilter,
  ] = useState("");

  const [
    provinceFilter,
    setProvinceFilter,
  ] = useState("");

  const [
    showInactive,
    setShowInactive,
  ] = useState(true);

  const [
    formOpen,
    setFormOpen,
  ] = useState(false);

  const [
    editingFacility,
    setEditingFacility,
  ] = useState(null);

  const [
    form,
    setForm,
  ] = useState(
    EMPTY_FORM
  );

  const [
    confirmState,
    setConfirmState,
  ] = useState({
    open: false,
    facility: null,
  });

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    statusSaving,
    setStatusSaving,
  ] = useState(false);

  const loadData = async () => {
    await loadFacilities({
      search,
      facilityType:
        facilityTypeFilter,
      province:
        provinceFilter,
      includeInactive:
        showInactive,
    });
  };

  useEffect(() => {
    loadData().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    facilityTypeFilter,
    provinceFilter,
    showInactive,
  ]);

  const filteredFacilities =
    useMemo(() => {
      const normalized =
        search
          .trim()
          .toLowerCase();

      if (!normalized) {
        return facilities;
      }

      return facilities.filter(
        (facility) => {
          return [
            facility.facility_name,
            facility.legal_name,
            facility.facility_type,
            facility.city,
            facility.province,
            facility.email,
          ].some((value) =>
            String(value || "")
              .toLowerCase()
              .includes(
                normalized
              )
          );
        }
      );
    }, [
      facilities,
      search,
    ]);

  const resetForm = () => {
    setForm(
      EMPTY_FORM
    );

    setEditingFacility(
      null
    );
  };

  const openCreateModal = () => {
    clearError();
    setSuccessMessage("");
    resetForm();
    setFormOpen(true);
  };

  const openEditModal = (
    facility
  ) => {
    clearError();
    setSuccessMessage("");

    setEditingFacility(
      facility
    );

    setForm({
      facilityName:
        facility.facility_name ||
        "",

      legalName:
        facility.legal_name ||
        "",

      facilityType:
        facility.facility_type ||
        "",

      contactPerson:
        facility.contact_person ||
        "",

      email:
        facility.email ||
        "",

      phone:
        facility.phone ||
        "",

      fax:
        facility.fax ||
        "",

      website:
        facility.website ||
        "",

      address:
        facility.address ||
        "",

      city:
        facility.city ||
        "",

      province:
        facility.province ||
        "",

      postalCode:
        facility.postal_code ||
        "",

      country:
        facility.country ||
        "Canada",

      description:
        facility.description ||
        "",

      notes:
        facility.notes ||
        "",

      status:
        facility.status ||
        "active",

      approvalStatus:
        facility.approval_status ||
        "pending",

      isActive:
        facility.is_active !==
        false,
    });

    setFormOpen(true);
  };

  const handleChange = (event) => {
  const { name, value } =
    event.target;

  setForm((current) => {
    /*
    ========================================
    KEEP STATUS + IS ACTIVE SYNCHRONIZED
    ========================================
    */

    if (name === "status") {
      const activeStatuses = [
        "active",
        "onboarding",
      ];

      return {
        ...current,
        status: value,
        isActive:
          activeStatuses.includes(
            value
          ),
      };
    }

    return {
      ...current,
      [name]: value,
    };
  });
};

  const saveFacility =
    async (event) => {
      event.preventDefault();

      if (
        !form.facilityName.trim()
      ) {
        return;
      }

      if (
        !form.facilityType
      ) {
        return;
      }

      try {
        setSaving(true);
        clearError();
        setSuccessMessage("");

        if (
          editingFacility
        ) {
          const result =
            await updateFacility(
              editingFacility.id,
              form
            );

          setSuccessMessage(
            result?.message ||
              "Facility updated successfully."
          );
        } else {
          const result =
            await createFacility(
              form
            );

          setSuccessMessage(
            result?.message ||
              "Facility created successfully."
          );
        }

        setFormOpen(false);
        resetForm();

        await loadData();
      } catch {
        // useFacilities already
        // stores error state.
      } finally {
        setSaving(false);
      }
    };

  const confirmToggleStatus = (
    facility
  ) => {
    setConfirmState({
      open: true,
      facility,
    });
  };

  const handleToggleStatus =
    async () => {
      const facility =
        confirmState.facility;

      if (!facility) {
        return;
      }

      try {
        setStatusSaving(true);
        clearError();
        setSuccessMessage("");

        const nextStatus =
          !facility.is_active;

        const result =
          await setFacilityStatus(
            facility.id,
            nextStatus
          );

        setSuccessMessage(
          result?.message ||
            "Facility status updated."
        );

        setConfirmState({
          open: false,
          facility: null,
        });

        await loadData();
      } catch {
        // Hook stores error
      } finally {
        setStatusSaving(false);
      }
    };

  const columns = [
    {
      key: "facility",
      label: "Facility",

      render: (row) => (
        <div className="facility-name-cell">
          <span className="facility-row-icon">
            <Building2
              size={19}
            />
          </span>

          <div>
            <strong>
              {
                row.facility_name
              }
            </strong>

            <small>
              ID: {row.id}
            </small>
          </div>
        </div>
      ),
    },

    {
      key: "facility_type",
      label: "Type",

      render: (row) =>
        row.facility_type ||
        "—",
    },

    {
      key: "location",
      label: "Location",

      render: (row) => (
        <div className="facility-location-cell">
          <MapPin
            size={15}
          />

          <span>
            {[
              row.city,
              row.province,
            ]
              .filter(Boolean)
              .join(", ") ||
              "—"}
          </span>
        </div>
      ),
    },

    {
      key: "approval",
      label: "Approval",

      render: (row) => (
        <StatusBadge
          status={
            row.approval_status ||
            "pending"
          }
        />
      ),
    },

    {
      key: "status",
      label: "Status",

      render: (row) => (
        <StatusBadge
          status={
            row.is_active
              ? "active"
              : "inactive"
          }
        />
      ),
    },

    {
      key: "contacts",
      label: "Contacts",

      render: (row) =>
        Number(
          row.contact_count ||
            0
        ),
    },

    {
      key: "contracts",
      label: "Contracts",

      render: (row) =>
        Number(
          row.contract_count ||
            0
        ),
    },

    {
      key: "documents",
      label: "Documents",

      render: (row) =>
        Number(
          row.document_count ||
            0
        ),
    },

    {
      key: "actions",
      label: "Actions",

      cellClassName:
        "facility-actions-cell",

      render: (row) => (
        <div className="facility-row-actions">
          <Button
            variant="secondary"
            size="small"
            icon={
              <Edit3
                size={15}
              />
            }
            onClick={() =>
              openEditModal(
                row
              )
            }
          >
            Edit
          </Button>

          <Button
            variant={
              row.is_active
                ? "danger"
                : "primary"
            }
            size="small"
            icon={
              <Power
                size={15}
              />
            }
            onClick={() =>
              confirmToggleStatus(
                row
              )
            }
          >
            {row.is_active
              ? "Deactivate"
              : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <main className="facility-management-page">
      <header className="facility-management-header">
        <div>
          <p className="facility-management-eyebrow">
            Administration
          </p>

          <h1>
            Facility Management
          </h1>

          <p>
            Manage healthcare
            facilities, approval
            status, contacts,
            contracts and compliance
            information.
          </p>
        </div>

     <div className="facility-header-actions">
  <Button
    variant="secondary"
    icon={
      <RefreshCw
        size={17}
      />
    }
    onClick={loadData}
    disabled={loading}
  >
    {loading ? "Refreshing..." : "Refresh"}
  </Button>

  <Button
    icon={
      <CirclePlus
        size={18}
      />
    }
    onClick={openCreateModal}
  >
    Add Facility
  </Button>
</div>
      </header>

      <section className="facility-stat-grid">
        <article>
          <Building2 />
          <div>
            <strong>
              {
                statistics.total
              }
            </strong>
            <span>
              Total Facilities
            </span>
          </div>
        </article>

        <article>
          <CheckCircle2 />
          <div>
            <strong>
              {
                statistics.active
              }
            </strong>
            <span>
              Active
            </span>
          </div>
        </article>

        <article>
          <ShieldCheck />
          <div>
            <strong>
              {
                statistics.pending_approval
              }
            </strong>
            <span>
              Pending Approval
            </span>
          </div>
        </article>

        <article>
          <Hospital />
          <div>
            <strong>
              {
                statistics.hospitals
              }
            </strong>
            <span>
              Hospitals
            </span>
          </div>
        </article>
      </section>

      {(error ||
        successMessage) && (
        <div
          className={`facility-feedback ${
            error
              ? "error"
              : "success"
          }`}
        >
          {error ||
            successMessage}
        </div>
      )}

      <section className="facility-table-panel">
        <TableToolbar
          left={
            <SearchBox
              value={search}
              onChange={
                setSearch
              }
              placeholder="Search facilities..."
            />
          }
          right={
            <>
              <select
                value={
                  facilityTypeFilter
                }
                onChange={(
                  event
                ) =>
                  setFacilityTypeFilter(
                    event.target
                      .value
                  )
                }
                className="facility-filter-select"
              >
                <option value="">
                  All facility types
                </option>

                {FACILITY_TYPES.map(
                  (option) => (
                    <option
                      key={
                        option.value
                      }
                      value={
                        option.value
                      }
                    >
                      {
                        option.label
                      }
                    </option>
                  )
                )}
              </select>

              <select
                value={
                  provinceFilter
                }
                onChange={(
                  event
                ) =>
                  setProvinceFilter(
                    event.target
                      .value
                  )
                }
                className="facility-filter-select"
              >
                <option value="">
                  All provinces
                </option>

                {PROVINCES.map(
                  (option) => (
                    <option
                      key={
                        option.value
                      }
                      value={
                        option.value
                      }
                    >
                      {
                        option.label
                      }
                    </option>
                  )
                )}
              </select>

              <label className="facility-inactive-toggle">
                <input
                  type="checkbox"
                  checked={
                    showInactive
                  }
                  onChange={(
                    event
                  ) =>
                    setShowInactive(
                      event.target
                        .checked
                    )
                  }
                />

                <span>
                  Show inactive
                </span>
              </label>
            </>
          }
        />

        <DataTable
          columns={columns}
          rows={
            filteredFacilities
          }
          loading={loading}
          emptyTitle="No facilities found"
          emptyMessage="No healthcare facilities match the current filters."
        />
      </section>

      <Modal
        open={formOpen}
        title={
          editingFacility
            ? "Edit Facility"
            : "Add Facility"
        }
        eyebrow="Facility Management"
        onClose={() => {
          if (!saving) {
            setFormOpen(
              false
            );

            resetForm();
          }
        }}
        size="large"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setFormOpen(
                  false
                );

                resetForm();
              }}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              form="facility-form"
              loading={saving}
            >
              {editingFacility
                ? "Save Changes"
                : "Create Facility"}
            </Button>
          </>
        }
      >
        <form
          id="facility-form"
          className="facility-form-grid"
          onSubmit={
            saveFacility
          }
        >
          <TextInput
            label="Facility name"
            name="facilityName"
            value={
              form.facilityName
            }
            onChange={
              handleChange
            }
            required
          />

          <TextInput
            label="Legal name"
            name="legalName"
            value={
              form.legalName
            }
            onChange={
              handleChange
            }
          />

          <SelectInput
            label="Facility type"
            name="facilityType"
            value={
              form.facilityType
            }
            onChange={
              handleChange
            }
            options={
              FACILITY_TYPES
            }
            required
          />

          <TextInput
            label="Primary contact"
            name="contactPerson"
            value={
              form.contactPerson
            }
            onChange={
              handleChange
            }
          />

          <TextInput
            label="Email"
            name="email"
            type="email"
            value={
              form.email
            }
            onChange={
              handleChange
            }
          />

          <TextInput
            label="Phone"
            name="phone"
            value={
              form.phone
            }
            onChange={
              handleChange
            }
          />

          <TextInput
            label="Fax"
            name="fax"
            value={
              form.fax
            }
            onChange={
              handleChange
            }
          />

          <TextInput
            label="Website"
            name="website"
            value={
              form.website
            }
            onChange={
              handleChange
            }
          />

          <div className="facility-form-full">
            <TextInput
              label="Address"
              name="address"
              value={
                form.address
              }
              onChange={
                handleChange
              }
            />
          </div>

          <TextInput
            label="City"
            name="city"
            value={
              form.city
            }
            onChange={
              handleChange
            }
          />

          <SelectInput
            label="Province"
            name="province"
            value={
              form.province
            }
            onChange={
              handleChange
            }
            options={
              PROVINCES
            }
          />

          <TextInput
            label="Postal code"
            name="postalCode"
            value={
              form.postalCode
            }
            onChange={
              handleChange
            }
          />

          <TextInput
            label="Country"
            name="country"
            value={
              form.country
            }
            onChange={
              handleChange
            }
          />

          <SelectInput
            label="Approval status"
            name="approvalStatus"
            value={
              form.approvalStatus
            }
            onChange={
              handleChange
            }
            options={
              APPROVAL_OPTIONS
            }
          />

          <SelectInput
            label="Operational status"
            name="status"
            value={
              form.status
            }
            onChange={
              handleChange
            }
            options={[
              {
                value:
                  "active",
                label:
                  "Active",
              },
              {
                value:
                  "inactive",
                label:
                  "Inactive",
              },
              {
                value:
                  "onboarding",
                label:
                  "Onboarding",
              },
              {
                value:
                  "suspended",
                label:
                  "Suspended",
              },
            ]}
          />

          <div className="facility-form-full">
            <label className="facility-textarea-field">
              <span>
                Description
              </span>

              <textarea
                name="description"
                value={
                  form.description
                }
                onChange={
                  handleChange
                }
                rows="4"
              />
            </label>
          </div>

          <div className="facility-form-full">
            <label className="facility-textarea-field">
              <span>
                Internal notes
              </span>

              <textarea
                name="notes"
                value={
                  form.notes
                }
                onChange={
                  handleChange
                }
                rows="4"
              />
            </label>
          </div>

          <div className="facility-form-full">
            <ToggleSwitch
  label="Facility is active"
  checked={form.isActive}
  onChange={(value) =>
    setForm((current) => ({
      ...current,

      isActive: value,

      status: value
        ? current.status ===
            "onboarding"
          ? "onboarding"
          : "active"
        : current.status ===
            "suspended"
          ? "suspended"
          : "inactive",
    }))
  }
/>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={
          confirmState.open
        }
        title={
          confirmState
            .facility
            ?.is_active
            ? "Deactivate Facility?"
            : "Activate Facility?"
        }
        message={
          confirmState
            .facility
            ?.is_active
            ? `Deactivate ${confirmState.facility?.facility_name}? The facility will remain in the system but will no longer be active.`
            : `Activate ${confirmState.facility?.facility_name}?`
        }
        confirmLabel={
          confirmState
            .facility
            ?.is_active
            ? "Deactivate"
            : "Activate"
        }
        variant={
          confirmState
            .facility
            ?.is_active
            ? "danger"
            : "primary"
        }
        loading={
          statusSaving
        }
        onCancel={() =>
          setConfirmState({
            open: false,
            facility: null,
          })
        }
        onConfirm={
          handleToggleStatus
        }
      />
    </main>
  );
};

export default FacilityManagement;